import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Dimmer, Grid, Loader } from "semantic-ui-react";
import { useGetAllProductsWithCalculatedSalesUnits } from "../../../../api/product";
import { useGetRecipeIngredient } from "../../../../api/recipe-ingredients";
import { useAddRecipeIngredient } from "../../../../api/recipeSimulator";
import { useGetStaticRecipeIngredientData } from "../../../../api/static-data";
import { DropDown } from "../../../../components";
import CommonSplitContainer from "../../../../components/commonSplitContainer/CommonSplitContainer";
import { InputNumber } from "../../../../components/InputText/InputNumber";
import MainBottomMultipleButtonView from "../../../../components/mainBottomButtonView/MainBottomMultipleButtonView";
import { RecipeIngredientType } from "../../../../config/enums/IngredientType.enum";
import { RECIPE_INGREDIENT_TYPE } from "../../../../config/optionsConstants";
import { errorView } from "../../../../helpers/ErrorHandler";
import { getCompanyTenantId } from "../../../../utils/cacheStorage";
import { pickNextColor } from "../../../../utils/recipeColor.utill";
import { RecipeAddIngredientModalProps } from "../types/recipeAddIngredient.types";
import GenericIngredientFields from "./GenaricIngredientFields";
import LegacySemanticModal from "./LegacySemanticModal";
import OwnProductFields from "./OwnProductsFields";

interface FormData {
  originalRecipePercentage: number | string;
}

const RecipeAddIngredientModalLegacy = ({
  visibleModal,
  setVisibleModal,
  recipeDataRefetch,
  recipeId,
  calculatedProductSalesUnit,
  genericIngredient: selectedGenericIngredient,
  usedColors
}: RecipeAddIngredientModalProps) => {
  const [ingredientType, setIngredientType] = useState<string>("");
  const [productName, setProductName] = useState<string>("");
  const [unitSize, setUnitSize] = useState<string>("");
  const [genericIngredient, setGenericIngredient] = useState<string>("");
  const [tenantId, setTenantId] = useState<string>("");
  const [saveType, setSaveType] = useState<"continue" | "new" | null>(null);

  const values = useMemo(() => {
    return {
      originalRecipePercentage: ""
    };
  }, []);

  const {
    formState: { errors },
    control,
    handleSubmit,
    reset,
    setValue
  } = useForm<FormData>({ defaultValues: values });

  const addIngredientMutation = useAddRecipeIngredient();

  useEffect(() => {
    const companyTenantId = getCompanyTenantId();
    if (companyTenantId) setTenantId(companyTenantId);
  }, []);

  const selectedSalesUnitIds = useMemo(() => {
    return calculatedProductSalesUnit.map(salesUnit => salesUnit.salesUnitId);
  }, [calculatedProductSalesUnit]);

  const selectedGenericIngredientIds = useMemo(() => {
    return selectedGenericIngredient.map(ingredient => ingredient.ingredientId);
  }, [selectedGenericIngredient]);

  const getIngredientTypeFlags = (type: string) => ({
    OwnProductCalculateLCA: type === RecipeIngredientType.OWN_PRODUCT,
    GenericIngredient: type === RecipeIngredientType.GENERIC_INGREDIENT
  });

  const { OwnProductCalculateLCA, GenericIngredient } = useMemo(
    () => getIngredientTypeFlags(ingredientType),
    [ingredientType]
  );

  const resetForm = useCallback(() => {
    setIngredientType("");
    setProductName("");
    setUnitSize("");
    setGenericIngredient("");
    reset({ originalRecipePercentage: "" });
  }, [reset]);

  const modalClose = useCallback(() => {
    setVisibleModal(false);
    resetForm();
  }, [setVisibleModal, resetForm]);

  const handleCreateProcessSuccess = (type: typeof saveType) => {
    switch (type) {
      case "continue":
        modalClose();
        break;
      case "new":
        resetForm();
        break;
      default:
        break;
    }
    setSaveType(null);
  };

  const { data: products, isLoading: isLoadingAllProductsWithCalculatedSalesUnits } =
    useGetAllProductsWithCalculatedSalesUnits();

  const {
    data: staticRecipeIngredients,
    isLoading: isLoadingStaticRecipeIngredients,
    isSuccess: isStaticRecipeIngredientsSuccess
  } = useGetStaticRecipeIngredientData();

  const {
    data: allowRecipeIngredients,
    isLoading: isLoadingAllowRecipeIngredients,
    isSuccess: isAllowRecipeIngredientsSuccess
  } = useGetRecipeIngredient(tenantId ?? "");

  const allowedIngredientIds = useMemo(() => {
    if (isAllowRecipeIngredientsSuccess && Array.isArray(allowRecipeIngredients)) {
      return allowRecipeIngredients.map((item: { ingredientId: number }) => item.ingredientId);
    }
    return [];
  }, [allowRecipeIngredients, isAllowRecipeIngredientsSuccess]);

  const genericIngredients = useMemo(() => {
    if (
      isStaticRecipeIngredientsSuccess &&
      Array.isArray(staticRecipeIngredients) &&
      Array.isArray(allowedIngredientIds)
    ) {
      return staticRecipeIngredients
        .filter(c => allowedIngredientIds.includes(Number(c.value)))
        .map(c => ({
          key: Number(c.key),
          text: String(c.text),
          value: Number(c.value),
          disabled: selectedGenericIngredientIds.includes(Number(c.value))
        }));
    }
    return [];
  }, [
    staticRecipeIngredients,
    isStaticRecipeIngredientsSuccess,
    allowedIngredientIds,
    selectedGenericIngredientIds
  ]);

  const ingredientSubmitData: Record<string, () => any> = {
    [RecipeIngredientType.OWN_PRODUCT]: () => {
      if (!productName || !unitSize) {
        errorView(`Please select the ${!productName ? "Product Name" : "Unit Size"}`);
        return;
      }
      return { productId: productName, salesUnitId: unitSize };
    },
    [RecipeIngredientType.GENERIC_INGREDIENT]: () => {
      if (!genericIngredient) {
        errorView("Please select the Generic Ingredient");
        return;
      }
      const ingredient = genericIngredients.find(
        g => g.value === Number(genericIngredient)
      );
      return {
        ingredientId: genericIngredient,
        ingredientName: ingredient ? ingredient.text : ""
      };
    }
  };

  const onSubmit = (formData: FormData) => {
    const percentage =
      formData.originalRecipePercentage === "" ? 0 : Number(formData.originalRecipePercentage);
    const data = ingredientSubmitData[ingredientType]();
    if (!data) return;
    data.percentage = percentage;
    data.chartColor = pickNextColor(usedColors);

    addIngredientMutation.mutate(
      { recipeId, type: ingredientType as RecipeIngredientType, data },
      {
        onSuccess: () => {
          handleCreateProcessSuccess(saveType);
          recipeDataRefetch();
        }
      }
    );
  };

  const handlePercentageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value === "" || value === "0.00") {
        setValue("originalRecipePercentage", "");
      }
    },
    [setValue]
  );

  if (
    isLoadingAllProductsWithCalculatedSalesUnits ||
    isLoadingStaticRecipeIngredients ||
    isLoadingAllowRecipeIngredients
  ) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <LegacySemanticModal
      modalOpenState={visibleModal}
      modalCloseState={modalClose}
      centered={true}
      title="Add an Ingredient to the Recipe"
    >
      <form onSubmit={handleSubmit(data => onSubmit(data))}>
        <Grid>
          <Grid.Column computer={16} tablet={16} mobile={16} className="userBankDetailsMain">
            <CommonSplitContainer
              currentHeight={0.91}
              topChild={
                <Grid>
                  <Grid.Column computer={8} tablet={8} mobile={16}>
                    <DropDown
                      labelName="Ingredient"
                      placeholder="Choose ingredient"
                      defaultValue={ingredientType}
                      currentData={RECIPE_INGREDIENT_TYPE}
                      customGridColumn={"customGridColomnTyp"}
                      handleChangeState={(_e: any, { value }: any) => {
                        setIngredientType(value);
                      }}
                    />
                  </Grid.Column>
                  {OwnProductCalculateLCA && (
                    <OwnProductFields
                      productName={productName}
                      setProductName={setProductName}
                      unitSize={unitSize}
                      setUnitSize={setUnitSize}
                      products={products}
                      selectedSalesUnitIds={selectedSalesUnitIds}
                    />
                  )}
                  {GenericIngredient && (
                    <GenericIngredientFields
                      genericIngredient={genericIngredient}
                      setGenericIngredient={setGenericIngredient}
                      genericIngredients={genericIngredients}
                    />
                  )}
                  {ingredientType && (
                    <Grid.Column computer={8} tablet={8} mobile={16}>
                      <InputNumber
                        transform={{
                          input: (value: number) =>
                            isNaN(value) || value === 0 ? "" : value.toString(),
                          output: (e: React.ChangeEvent<HTMLInputElement>) => e
                        }}
                        control={control}
                        labelName="Baseline Recipe %*"
                        placeholder="0.00"
                        name="originalRecipePercentage"
                        errors={errors?.originalRecipePercentage}
                        errorMessage="Baseline Recipe % is required"
                        onChangeFunction={handlePercentageChange}
                        otherValidation={{
                          validate: {
                            valueCheck: (v: string | number) =>
                              v === "" ||
                              (Number(v) >= 0 && Number(v) <= 100) ||
                              "Baseline Recipe % must be greater than 0 and up to 100"
                          }
                        }}
                        enableDecimalWithMinus={false}
                        removeDecimal={false}
                      />
                    </Grid.Column>
                  )}
                </Grid>
              }
              bottomChild={
                <MainBottomMultipleButtonView
                  leftButtons={[
                    {
                      title: "Cancel",
                      onClick: modalClose,
                      buttonOutLine: true
                    }
                  ]}
                  rightButtons={[
                    {
                      contentView: 8,
                      title: "Save",
                      onClick: () => {
                        setSaveType("continue");
                      },
                      type: "submit",
                      buttonView: true,
                      buttonOutLine: true
                    },
                    {
                      contentView: 8,
                      title: "Save and Add Next Ingredient",
                      onClick: () => setSaveType("new"),
                      type: "submit",
                      buttonView: true
                    }
                  ]}
                />
              }
            />
          </Grid.Column>
        </Grid>
      </form>
    </LegacySemanticModal>
  );
};

export default RecipeAddIngredientModalLegacy;
